/**
 * Page de connexion pour les clients publics
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Divider,
  Chip
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { login, clearError } from '../../redux/slices/clientAuthSlice';
import { RootState, AppDispatch } from '../../redux/store';

export default function ClientLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading, error } = useSelector(
    (state: RootState) => state.clientAuth
  );

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { user } = useSelector((state: RootState) => state.clientAuth);

  useEffect(() => {
    console.log('[ClientLogin] useEffect triggered:', {
      isAuthenticated,
      hasUser: !!user,
      userType: (user as any)?.user_type,
      userRole: (user as any)?.role
    });

    if (isAuthenticated && user) {
      // IMPORTANT: Vérifier que le token est bien dans localStorage avant de rediriger
      // pour éviter une race condition où le dashboard se charge avant que le token soit écrit
      const token = localStorage.getItem('clientAccessToken');
      if (!token) {
        console.log('[ClientLogin] Token not yet in localStorage, waiting...');
        return;
      }

      console.log('[ClientLogin] Token confirmed in localStorage, proceeding with redirect');

      // Si c'est un utilisateur cyber qui se connecte, rediriger vers le dashboard cyber
      if ((user as any).user_type === 'cyber' || (user as any).role === 'cyber' || (user as any).role === 'admin') {
        console.log('[ClientLogin] Redirecting cyber to /cyber-dashboard');
        navigate('/cyber-dashboard');
      } else {
        console.log('[ClientLogin] Redirecting client to /client/dashboard');
        navigate('/client/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'L\'email ou identifiant ANEF est requis';
    }
    // Pas de validation d'email car peut être un identifiant ANEF

    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());

    if (!validate()) return;

    console.log('[ClientLogin] Submitting login with email:', formData.email);
    const result = await dispatch(login(formData));

    console.log('[ClientLogin] Login dispatch result:', {
      type: result.type,
      isFulfilled: login.fulfilled.match(result),
      isRejected: login.rejected.match(result)
    });

    if (login.fulfilled.match(result)) {
      console.log('[ClientLogin] Login successful, payload:', result.payload);
      // Vérifier immédiatement le localStorage
      const token = localStorage.getItem('clientAccessToken');
      const user = localStorage.getItem('clientUser');
      console.log('[ClientLogin] After login - localStorage check:', {
        hasToken: !!token,
        tokenPreview: token?.substring(0, 20) + '...',
        hasUser: !!user,
        userData: user ? JSON.parse(user) : null
      });
      // La redirection se fera automatiquement via useEffect en fonction du rôle
    } else {
      console.error('[ClientLogin] Login failed:', result.payload);
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' });
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        {/* Header with Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/logo.svg"
            alt="Logo"
            sx={{
              height: 60,
              mb: 3,
              filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))'
            }}
          />
          <Typography variant="h4" gutterBottom>
            Connexion Client
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accédez à votre espace de suivi ANEF
          </Typography>
        </Box>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email, Identifiant ANEF ou Nom d'utilisateur"
            type="text"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!validationErrors.email}
            helperText={validationErrors.email || 'Votre email, numéro ANEF ou nom d\'utilisateur fourni par votre cyber'}
            margin="normal"
            autoComplete="username"
            autoFocus
          />

          <TextField
            fullWidth
            label="Mot de passe"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange('password')}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Link
              component={RouterLink}
              to="/client/forgot-password"
              variant="body2"
              underline="hover"
            >
              Mot de passe oublié ?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            startIcon={<LoginIcon />}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Chip label="OU" size="small" />
        </Divider>

        {/* Inscription */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Pas encore de compte ?
          </Typography>
          <Button
            component={RouterLink}
            to="/client/register"
            variant="outlined"
            fullWidth
          >
            Créer un compte gratuit
          </Button>
        </Box>

        {/* Info */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="caption">
            <strong>Nouveau client ?</strong> Inscrivez-vous avec vos identifiants ANEF
            pour bénéficier d'un suivi automatique gratuit de vos dossiers.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
}
